import { message, Modal } from 'antd';
import { fuelEfficiencyApi, userSettingsApi } from '../../services/api';

interface FuelEfficiencyCalculationParams {
  vehicleId: number;
  t: (key: string) => string;
  isMobile: boolean;
  onSuccess: () => void;
  setLoading: (loading: boolean) => void;
}

export const handleCalculateFuelEfficiency = async ({
  vehicleId,
  t,
  isMobile,
  onSuccess,
  setLoading
}: FuelEfficiencyCalculationParams) => {
  try {
    setLoading(true);

    // Read fuel station coordinates from user settings (configured on /settings page)
    let fuelStationLat = 0;
    let fuelStationLon = 0;
    let radiusKm = 0.06;

    try {
      const settingsRes = await userSettingsApi.getCurrentUserSettings();
      const settings = settingsRes.data;
      if (settings?.fuelStationLat && settings?.fuelStationLon) {
        fuelStationLat = settings.fuelStationLat;
        fuelStationLon = settings.fuelStationLon;
      }
    } catch (e) {
      console.warn('Could not load user settings for fuel station location', e);
    }

    if (!fuelStationLat || !fuelStationLon) {
      Modal.warning({
        title: 'Fuel Station Not Configured',
        content: (
          <div>
            <p>Please set the fuel station location (latitude & longitude) on the Settings page before calculating fuel efficiency.</p>
            <p><strong>Settings → Fuel Station Location</strong></p>
          </div>
        ),
        width: isMobile ? '95%' : 500
      });
      return;
    }

    const fromDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const response = await fuelEfficiencyApi.calculateVcomsat(
      vehicleId,
      fuelStationLat,
      fuelStationLon,
      fromDate,
      undefined,
      radiusKm
    );
    
    if (response.data.success) {
      message.success(`Calculated ${response.data.calculatedCount} fuel efficiency records using GPS data`);
      
      const summaryRes = await fuelEfficiencyApi.getSummary(vehicleId);
      Modal.info({
        title: 'Fuel Efficiency Summary (GPS-based)',
        content: (
          <div>
            <p><strong>Method:</strong> VCOMSAT GPS Tracking</p>
            <p><strong>Average:</strong> {summaryRes.data.averageLitersPer100Km?.toFixed(2)} L/100km</p>
            <p><strong>Best:</strong> {summaryRes.data.bestLitersPer100Km?.toFixed(2)} L/100km</p>
            <p><strong>Worst:</strong> {summaryRes.data.worstLitersPer100Km?.toFixed(2)} L/100km</p>
            <p><strong>Total Distance:</strong> {summaryRes.data.totalDistanceKm?.toFixed(0)} km</p>
            <p><strong>Total Fuel:</strong> {summaryRes.data.totalFuelLiters?.toFixed(2)} L</p>
            <p><strong>Periods:</strong> {summaryRes.data.totalIntervals}</p>
            <hr />
            <small style={{ color: '#666' }}>
              ✓ No odometer readings required<br />
              ✓ Uses GPS distance from VCOMSAT<br />
              ✓ Fuel station: {fuelStationLat.toFixed(6)}, {fuelStationLon.toFixed(6)}
            </small>
          </div>
        ),
        width: isMobile ? '95%' : 500
      });

      onSuccess();
    } else {
      message.warning(response.data.message);
    }
  } catch (error: any) {
    console.error('Fuel efficiency calculation error:', error);
    const errorMessage = error?.response?.data?.message || error?.message || 'Failed to calculate fuel efficiency';
    
    Modal.error({
      title: 'Calculation Failed',
      content: (
        <div>
          <p>{errorMessage}</p>
          <hr />
          <p><strong>Common issues:</strong></p>
          <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
            <li>Vehicle not synced with VCOMSAT - Go to Vehicles → Sync with VCOMSAT</li>
            <li>No GPS data at fuel station location - Check fuel station coordinates</li>
            <li>Need at least 2 fuel receipts in the system</li>
            <li>VCOMSAT credentials not configured</li>
          </ul>
        </div>
      ),
      width: isMobile ? '95%' : 500
    });
  } finally {
    setLoading(false);
  }
};
